import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { ComponentStore } from '@ngrx/component-store';
import { NotificationJsonModel } from '@siza-staff-care/shared/data';
import { Observable, tap, withLatestFrom } from 'rxjs';

interface ShowMessageOptions {
	msg: string;
	horizontalPosition?: MatSnackBarHorizontalPosition;
	verticalPosition?: MatSnackBarVerticalPosition;
}

@Injectable({
	providedIn: 'root',
})
export class SnackbarService {
	constructor(private store: SnackbarStore) {}

	/**
	 * Shows a snackbar with the given message, using the defaults.
	 * @param msg
	 */
	showMessage(
		msg: string,
		opts?: { horizontalPosition?: MatSnackBarHorizontalPosition; verticalPosition?: MatSnackBarVerticalPosition },
	) {
		this.store.showMessage({ msg, ...opts });
	}

	showErrorMessage(msg: string) {
		this.store.showErrorMessage(msg);
	}

	showNotificationMessage(note: NotificationJsonModel, verb = 'Updated') {
		const staffCode = note.note_causer_staffCode || 'system';
		this.showMessage(`${verb} by ${staffCode}`);
	}
}

interface SnackbarState {
	suppress?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class SnackbarStore extends ComponentStore<SnackbarState> {
	constructor(private snackbar: MatSnackBar) {
		super({});
	}

	private readonly suppress$ = this.select(({ suppress }) => !!suppress);

	readonly setSuppress = this.updater((state, suppress: boolean) => ({
		...state,
		suppress,
	}));

	readonly showMessage = this.effect((opts$: Observable<ShowMessageOptions>) =>
		opts$.pipe(
			withLatestFrom(this.suppress$),
			tap(([opts, suppress]) => {
				if (suppress) {
					return;
				}

				this.snackbar.open(opts.msg, '', {
					horizontalPosition: opts?.horizontalPosition || 'end',
					verticalPosition: opts?.verticalPosition || 'bottom',
				});
			}),
		),
	);

	readonly showErrorMessage = this.effect((msg$: Observable<string>) =>
		msg$.pipe(
			tap((msg) => {
				this.snackbar.open(msg, 'Close', {
					duration: 0,
					horizontalPosition: 'center',
					verticalPosition: 'top',
					panelClass: 'error',
				});
			}),
		),
	);
}
